import { useEffect, useState, useCallback } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useNoteShortcuts } from '@/hooks/useNoteShortcuts';
import Sidebar from '@/components/Sidebar';
import NoteList from '@/components/NoteList';
import Editor from '@/components/Editor';
import GraphView from '@/components/GraphView';
import JournalView from '@/components/JournalView';
import FAB from '@/components/FAB';
import CommandPalette from '@/components/CommandPalette';
import QuickCapture from '@/components/QuickCapture';
import BackupReminder from '@/components/BackupReminder';
import OfflineIndicator from '@/components/OfflineIndicator';
import KeyboardShortcuts from '@/components/KeyboardShortcuts';
import SyncConflictDialog from '@/components/SyncConflictDialog';
import SettingsDialog from '@/components/SettingsDialog';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import '@/App.css';

export default function AppLayout() {
  const { 
    sidebarOpen, setSidebarOpen, selectedNoteId, setEditorMode, editorMode, loading,
    syncConflict, resolveSyncConflict, dismissSyncConflict, activeView, setActiveView 
  } = useApp();
  const [showSettings, setShowSettings] = useState(false);
  const [showQuickCapture, setShowQuickCapture] = useState(false);
  const openQuickCapture = useCallback(() => setShowQuickCapture(true), []);

  // Enable global note shortcuts
  useNoteShortcuts();

  // Handle PWA shortcuts and share-target via URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const action = params.get('action');
    if (action) {
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      switch (action) {
        case 'quick-capture':
          setShowQuickCapture(true);
          break;
        case 'new-note':
          // Handled by AppContext createNote
          break;
        case 'daily-note':
          // Will be handled by event
          break;
        case 'share': {
          const text = params.get('text') || '';
          const url = params.get('url') || '';
          const title = params.get('title') || '';
          const content = [title, text, url].filter(Boolean).join('\n\n');
          if (content) {
            setShowQuickCapture(true);
          }
          break;
        }
        default: break;
      }
    }
  }, []);

  // Auto-switch to 'edit' on mobile when split view is active
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const handler = (e) => {
      if (e.matches && editorMode === 'split') {
        setEditorMode('edit');
      }
    };
    handler(mq);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [editorMode, setEditorMode]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background" data-testid="app-loading">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-muted-foreground/30 border-t-foreground rounded-full animate-spin" />
          <span className="text-sm text-muted-foreground font-mono">Loading notes...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[100dvh] bg-background overflow-hidden" data-testid="app-layout">
      {/* Desktop sidebar — only visible lg+ */}
      <div className="hidden lg:flex flex-shrink-0 w-[250px] border-r border-border bg-secondary/30">
        <Sidebar />
      </div>

      {/* Mobile / tablet sidebar — Sheet overlay */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-[280px] sm:w-[300px] p-0 bg-secondary/95 border-border lg:hidden" data-testid="mobile-sidebar-sheet">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <Sidebar onNavigate={() => setSidebarOpen(false)} />
        </SheetContent>
      </Sheet>

      {/*
        Mobile drill-down:
          - No note selected → show NoteList (full width)
          - Note selected → show Editor (full width)
        Tablet (md-lg): NoteList (300px) + Editor
        Desktop (lg+): Sidebar (250px) + NoteList (300px) + Editor
      */}

      {/* Graph View — replaces NoteList+Editor when active */}
      {activeView === 'graph' ? (
        <div className="flex-1 min-w-0">
          <GraphView onClose={() => setActiveView('all')} />
        </div>
      ) : activeView === 'journal' ? (
        <>
          {/* Journal: JournalView panel (acts like NoteList) + Editor */}
          <div
            className={`
              flex-shrink-0 border-r border-border bg-background overflow-hidden
              w-full md:w-[380px] md:max-w-[380px]
              ${selectedNoteId ? 'hidden md:flex' : 'flex'}
            `}
          >
            <JournalView />
          </div>
          <div
            className={`
              flex-1 min-w-0
              ${selectedNoteId ? 'flex' : 'hidden md:flex'}
            `}
          >
            <Editor />
          </div>
        </>
      ) : (
        <>
          {/* NoteList panel */}
          <div
            className={`
              flex-shrink-0 border-r border-border bg-background overflow-hidden
              w-full md:w-[340px] md:max-w-[340px]
              ${selectedNoteId ? 'hidden md:flex' : 'flex'}
            `}
          >
            <NoteList />
          </div>

          {/* Editor panel */}
          <div
            className={`
              flex-1 min-w-0
              ${selectedNoteId ? 'flex' : 'hidden md:flex'}
            `}
          >
            <Editor />
          </div>
        </>
      )}

      <FAB />
      <CommandPalette onOpenQuickCapture={openQuickCapture} />
      <QuickCapture open={showQuickCapture} onClose={() => setShowQuickCapture(false)} />
      
      {/* Keyboard Shortcuts Help */}
      <KeyboardShortcuts />
      
      {/* Offline Indicator */}
      <OfflineIndicator />
      
      {/* Backup Reminder */}
      <BackupReminder onOpenSettings={() => setShowSettings(true)} />
      
      {/* Settings Dialog */}
      {showSettings && <SettingsDialog onClose={() => setShowSettings(false)} />}
      
      {/* Sync Conflict Dialog */}
      <SyncConflictDialog
        conflict={syncConflict}
        onResolve={resolveSyncConflict}
        onClose={dismissSyncConflict}
      />
    </div>
  );
}
